import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, today } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, CheckCircle2, XCircle, Clock, Send } from "lucide-react";
import { toast } from "sonner";

const STATUS_TABS = [
    { v: "all", label: "All" },
    { v: "pending_approval", label: "Pending approval", icon: Clock },
    { v: "posted", label: "Posted", icon: CheckCircle2 },
    { v: "rejected", label: "Rejected", icon: XCircle },
];

const STATUS_BADGE = {
    pending_approval: { label: "Pending", color: "text-[#c97a3a]", bg: "bg-[#c97a3a]/12" },
    posted: { label: "Posted", color: "text-[#1f6c47]", bg: "bg-[#1f6c47]/12" },
    rejected: { label: "Rejected", color: "text-[#b4233a]", bg: "bg-[#b4233a]/12" },
    void: { label: "Void", color: "text-muted-foreground", bg: "bg-muted/40" },
};

export default function ManualJournals() {
    const { user } = useAuth();
    const role = (user?.role || "").toLowerCase();
    const canApprove = ["founder", "super admin", "super_admin", "accountant", "finance manager"].includes(role);
    const isFounder = ["founder", "super admin", "super_admin"].includes(role);

    const [items, setItems] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState("all");
    const [form, setForm] = useState({
        date: today(), narration: "", reference: "", auto_post: false,
        lines: [{ account_id: "", debit: 0, credit: 0, description: "" }, { account_id: "", debit: 0, credit: 0, description: "" }],
    });
    const [rejectFor, setRejectFor] = useState(null);
    const [rejectReason, setRejectReason] = useState("");

    const load = async () => {
        const [j, a] = await Promise.all([api.get("/journals", { params: { source: "manual" } }), api.get("/coa")]);
        setItems(j.data); setAccounts(a.data);
    };
    useEffect(() => { load(); }, []);

    const setLine = (i, k, v) => setForm((f) => { const ls = [...f.lines]; ls[i] = { ...ls[i], [k]: v }; return { ...f, lines: ls }; });
    const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { account_id: "", debit: 0, credit: 0, description: "" }] }));
    const rmLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

    const totals = (() => {
        const d = form.lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
        const c = form.lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
        return { d, c, ok: Math.abs(d - c) < 0.01 && d > 0 };
    })();

    const submit = async (e) => {
        e.preventDefault();
        if (!totals.ok) return toast.error("Debits must equal credits");
        try {
            const res = await api.post("/journals", { ...form, lines: form.lines.filter((l) => l.account_id) });
            toast.success(res.data.status === "posted" ? "Journal posted" : "Submitted for approval");
            setOpen(false);
            setForm({ date: today(), narration: "", reference: "", auto_post: false, lines: [{ account_id: "", debit: 0, credit: 0, description: "" }, { account_id: "", debit: 0, credit: 0, description: "" }] });
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const approve = async (id) => {
        try {
            await api.post(`/journals/${id}/approve`);
            toast.success("Journal approved & posted");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Approve failed");
        }
    };

    const reject = async () => {
        if (!rejectFor) return;
        try {
            await api.post(`/journals/${rejectFor}/reject`, { reason: rejectReason || "Rejected" });
            toast.success("Journal rejected");
            setRejectFor(null); setRejectReason("");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reject failed");
        }
    };

    const filtered = tab === "all" ? items : items.filter((j) => j.status === tab);
    const pendingCount = items.filter((j) => j.status === "pending_approval").length;

    return (
        <>
            <PageHeader
                kicker="ACCOUNTING"
                title="Manual Journals"
                subtitle="Adjusting entries, accruals, and manual postings — with submit & approve workflow."
                actions={<Button onClick={() => setOpen(true)} data-testid="journal-new"><Plus className="w-4 h-4 mr-1.5" />New journal</Button>}
            />
            <PageBody>
                {/* Tabs */}
                <div className="flex flex-wrap gap-1 mb-4 border-b border-border">
                    {STATUS_TABS.map((t) => {
                        const Icon = t.icon;
                        const isActive = tab === t.v;
                        return (
                            <button
                                key={t.v}
                                onClick={() => setTab(t.v)}
                                data-testid={`journal-tab-${t.v}`}
                                className={`flex items-center gap-1.5 px-3 py-2 -mb-px border-b-2 text-[12.5px] font-medium transition ${isActive ? "border-[#a68a56] text-[#13192e]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                            >
                                {Icon && <Icon className="w-3.5 h-3.5" />}
                                {t.label}
                                {t.v === "pending_approval" && pendingCount > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#c97a3a] text-white text-[10px] font-bold">{pendingCount}</span>}
                            </button>
                        );
                    })}
                </div>

                <div className="bg-card border border-border rounded-md overflow-hidden">
                    <table className="fin-table">
                        <thead><tr><th>Number</th><th>Date</th><th>Narration</th><th>Status</th><th className="num">Amount</th><th></th></tr></thead>
                        <tbody>
                            {filtered.map((j) => {
                                const sb = STATUS_BADGE[j.status] || { label: j.status, color: "text-muted-foreground", bg: "bg-muted/40" };
                                return (
                                    <tr key={j.id} data-testid={`journal-row-${j.id}`}>
                                        <td className="font-mono text-[12px] font-medium">{j.number}</td>
                                        <td className="text-[12px] text-muted-foreground">{fmtDate(j.date)}</td>
                                        <td>
                                            {j.narration}
                                            {j.status === "rejected" && j.rejection_reason && (
                                                <div className="text-[11px] text-[#b4233a] mt-0.5">— {j.rejection_reason}</div>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded ${sb.bg} ${sb.color}`}>{sb.label}</span>
                                        </td>
                                        <td className="num">{fmtMoney(j.total_debit)}</td>
                                        <td className="text-right whitespace-nowrap">
                                            {j.status === "pending_approval" && canApprove && (
                                                <>
                                                    <Button size="sm" variant="outline" className="mr-1" onClick={() => approve(j.id)} data-testid={`approve-${j.id}`}>
                                                        <CheckCircle2 className="w-3.5 h-3.5 mr-1 text-[#1f6c47]" /> Approve
                                                    </Button>
                                                    <Button size="sm" variant="ghost" onClick={() => setRejectFor(j.id)} data-testid={`reject-${j.id}`}>
                                                        <XCircle className="w-3.5 h-3.5 mr-1 text-[#b4233a]" /> Reject
                                                    </Button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-muted-foreground text-[12px]">No journals in this view.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </PageBody>

            {/* New journal sheet */}
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side="right" className="w-full sm:max-w-[680px] p-0 flex flex-col">
                    <SheetHeader className="border-b border-border px-6 py-4"><SheetTitle className="text-xl font-semibold tracking-tight">New journal entry</SheetTitle></SheetHeader>
                    <form className="flex-1 overflow-y-auto p-6 space-y-4" onSubmit={submit}>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
                            <div><Label>Reference</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1.5" /></div>
                        </div>
                        <div><Label>Narration</Label><Input value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} className="mt-1.5" /></div>
                        <div className="border-t border-border pt-3">
                            <Label>Lines</Label>
                            <div className="space-y-2 mt-2">
                                {form.lines.map((l, i) => (
                                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                                        <div className="col-span-5">
                                            <Select value={l.account_id} onValueChange={(v) => setLine(i, "account_id", v)}>
                                                <SelectTrigger className="h-9"><SelectValue placeholder="Account" /></SelectTrigger>
                                                <SelectContent>
                                                    {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} • {a.name}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <Input className="col-span-3 h-9" type="number" placeholder="Debit" value={l.debit} onChange={(e) => setLine(i, "debit", Number(e.target.value))} />
                                        <Input className="col-span-3 h-9" type="number" placeholder="Credit" value={l.credit} onChange={(e) => setLine(i, "credit", Number(e.target.value))} />
                                        <Button type="button" variant="ghost" size="icon" className="col-span-1 h-9 w-9" onClick={() => rmLine(i)}><Trash2 className="w-4 h-4" /></Button>
                                    </div>
                                ))}
                            </div>
                            <Button type="button" variant="outline" size="sm" onClick={addLine} className="mt-2"><Plus className="w-3.5 h-3.5 mr-1" />Add line</Button>
                        </div>
                        <div className="flex items-center justify-between border-t border-border pt-3 text-[13px]">
                            <div>Debit total: <span className="font-mono">{fmtMoney(totals.d)}</span></div>
                            <div>Credit total: <span className="font-mono">{fmtMoney(totals.c)}</span></div>
                            <div className={totals.ok ? "text-positive" : "text-destructive"}>{totals.ok ? "Balanced" : "Unbalanced"}</div>
                        </div>
                        {isFounder && (
                            <label className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
                                <input type="checkbox" checked={form.auto_post} onChange={(e) => setForm({ ...form, auto_post: e.target.checked })} data-testid="auto-post-toggle" />
                                Auto-post (skip approval — Founder/Admin only)
                            </label>
                        )}
                        <Button type="submit" className="w-full" disabled={!totals.ok} data-testid="journal-save">
                            {form.auto_post ? <><CheckCircle2 className="w-4 h-4 mr-1.5" />Post entry directly</> : <><Send className="w-4 h-4 mr-1.5" />Submit for approval</>}
                        </Button>
                    </form>
                </SheetContent>
            </Sheet>

            {/* Reject reason sheet */}
            <Sheet open={!!rejectFor} onOpenChange={(v) => !v && setRejectFor(null)}>
                <SheetContent side="right" className="w-full sm:max-w-[400px] p-0 flex flex-col">
                    <SheetHeader className="border-b border-border px-6 py-4"><SheetTitle className="text-xl font-semibold tracking-tight">Reject journal</SheetTitle></SheetHeader>
                    <div className="flex-1 p-6 space-y-3">
                        <Label>Reason</Label>
                        <textarea
                            className="w-full border border-border rounded-md px-3 py-2 text-sm min-h-[120px]"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Wrong account selected, missing supporting doc, etc."
                            data-testid="reject-reason"
                        />
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setRejectFor(null)} className="flex-1">Cancel</Button>
                            <Button onClick={reject} className="flex-1" data-testid="reject-confirm">Confirm rejection</Button>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
}
